import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, FileText, X, Loader2, AlertCircle, CheckCircle2, FileCode, BookOpen, Building2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/supabase';
import ReExtractWarningDialog from '../components/ReExtractWarningDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FileDropZone({ label, description, icon: Icon, accept, files, onDrop, onRemove, disabled, testId }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: label === 'O&M Manuals',
    disabled,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-accent" />
        <h3 className="font-semibold text-sm text-foreground">{label}</h3>
      </div>
      <div
        {...getRootProps()}
        data-testid={testId}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${isDragActive ? 'border-accent bg-accent/10 scale-[1.01]' : 'border-border hover:border-accent/50 hover:bg-muted/30'}
          ${disabled ? 'opacity-40 pointer-events-none' : ''}
        `}
      >
        <input {...getInputProps()} />
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div key={f.id} className="flex items-center justify-between p-2 bg-muted/60 rounded-md text-sm">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate text-foreground">{f.name}</span>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {f.size < 1048576 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}
                </span>
              </div>
              <button onClick={() => onRemove(f.id)} disabled={disabled}
                className="ml-2 p-1 hover:bg-destructive/10 rounded">
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PROCESSING_STEPS = [
  { key: 'upload', label: 'Uploading files...' },
  { key: 'parsing', label: 'Parsing IFC model...' },
  { key: 'bep', label: 'Extracting BEP rules with AI...' },
  { key: 'validating', label: 'Running 25-rule validation...' },
  { key: 'scoring', label: 'Calculating confidence scores...' },
  { key: 'register', label: 'Building AIM Register...' },
];

export default function NewAuditPage() {
  const [ifcFiles, setIfcFiles] = useState([]);
  const [bepFiles, setBepFiles] = useState([]);
  const [omFiles, setOmFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processingStep, setProcessingStep] = useState('');
  const [auditId, setAuditId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [duplicateInfo, setDuplicateInfo] = useState(null); // {project_name, project_code, file_name, version, analyzed_at, rules_count, has_stored_rules, slug, candidate_rules}
  const [showReExtract, setShowReExtract] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch real projects from DB and pre-select via ?project=CODE if provided
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch(`${API_URL}/api/projects`);
        if (resp.ok) {
          const data = await resp.json();
          const list = (data.projects || []).filter(p => p.code !== 'DEFAULT');
          if (cancelled) return;
          setProjects(list);
          // Pre-select from query string (?project=VOC) or first project as default
          const fromQuery = searchParams.get('project');
          let initialId = '';
          if (fromQuery) {
            const match = list.find(p => p.code === fromQuery || p.id === fromQuery);
            if (match) initialId = match.id;
          }
          if (!initialId && list.length > 0) initialId = list[0].id;
          setSelectedProjectId(initialId);
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  const runBepExtraction = async ({ force, projectName, projectCode }) => {
    setProcessingStep(`Uploading BEP for ${projectName}${force ? ' (forced re-extract)' : ''}...`);
    const formData = new FormData();
    formData.append('bep_file', bepFiles[0].file);
    const url = `${API_URL}/api/projects/${selectedProjectId}/upload-bep${force ? '?force=true' : ''}`;
    const resp = await apiFetch(url, { method: 'POST', body: formData });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'BEP upload failed');
    }
    const data = await resp.json();
    if (data.already_analyzed) {
      // Don't auto-redirect. Show duplicate panel with View/Re-extract options.
      setDuplicateInfo({
        project_name: data.project_name || projectName,
        project_code: data.project_code || projectCode,
        slug: data.project_id || projectCode,
        file_name: data.bep_file_name || bepFiles[0].name,
        document_version: data.document_version || '',
        analyzed_at: data.analyzed_at,
        rules_count: data.rules_count || 0,
        has_stored_rules: !!data.has_stored_rules,
        candidate_rules: data.candidate_rules || [],
        extracted_by: data.extracted_by || '',
      });
      setProcessingStep('');
      setLoading(false);
      return;
    }
    if (data.job_id) {
      setProcessingStep(`AI is extracting rules for ${projectName} (2-3 minutes)...`);
      const slug = data.project_id || projectCode;
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const sr = await apiFetch(`${API_URL}/api/projects/${slug}/bep-status/${data.job_id}`);
          if (sr.ok) {
            const s = await sr.json();
            setProcessingStep(s.step || 'Processing...');
            if (s.status === 'completed' || s.status === 'completed_with_warnings') {
              done = true;
              navigate(`/projects/${slug}`);
            }
            if (s.status === 'failed') throw new Error(s.error || 'Extraction failed');
          }
        } catch (e) { if (e.message.includes('failed')) throw e; }
      }
      // Final check before declaring timeout
      try {
        const bepResp = await apiFetch(`${API_URL}/api/projects/${slug}/bep`);
        if (bepResp.ok) {
          const bepData = await bepResp.json();
          if (bepData.has_bep && bepData.bep.candidate_rules && bepData.bep.candidate_rules.length > 0) {
            navigate(`/projects/${slug}`);
            return;
          }
        }
      } catch (finalCheckErr) {}
      if (!done) throw new Error('Extraction timed out');
      return;
    }
    navigate(`/projects/${data.project_id || projectCode}`);
  };

  const makeFileObj = (file) => ({
    file, id: Math.random().toString(36).substring(7),
    name: file.name, size: file.size,
  });

  const onDropIfc = useCallback((accepted, rejected) => {
    if (rejected.length) { setError('Only .ifc files accepted'); return; }
    setError('');
    setIfcFiles(accepted.map(makeFileObj));
  }, []);

  const onDropBep = useCallback((accepted, rejected) => {
    if (rejected.length) { setError('Only .pdf files accepted for BEP'); return; }
    setError('');
    setBepFiles(accepted.map(makeFileObj));
  }, []);

  const onDropOm = useCallback((accepted, rejected) => {
    if (rejected.length) { setError('Only .pdf files accepted for O&M'); return; }
    setError('');
    setOmFiles(prev => [...prev, ...accepted.map(makeFileObj)]);
  }, []);

  // (Removed Feb 26, 2026) — in-place ``pollStatus`` polled here for up
  // to 10 minutes and silently timed out, dumping the user on the
  // results dashboard mid-audit. The dedicated ``AuditProgressPage``
  // now owns the polling: this page only POSTs the upload and then
  // navigates to ``/audits/:id/progress``.

  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

  const uploadFileChunked = async (file, fileType, uploadId) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const form = new FormData();
      form.append('file', chunk, file.name);
      form.append('upload_id', uploadId);
      form.append('chunk_index', i.toString());
      form.append('total_chunks', totalChunks.toString());
      form.append('file_type', fileType);
      form.append('file_name', file.name);

      setProcessingStep(`Uploading ${fileType.toUpperCase()} (${Math.round(((i+1)/totalChunks)*100)}%)...`);

      const resp = await apiFetch(`${API_URL}/api/audits/upload-chunk`, { method: 'POST', body: form });
      if (!resp.ok) throw new Error(`Chunk upload failed for ${fileType}`);
    }
  };

  const [namingMismatch, setNamingMismatch] = useState(null); // {errors}
  const runAudit = async ({ confirmMismatch = false } = {}) => {
    const hasIfc = ifcFiles.length > 0;
    const hasBep = bepFiles.length > 0;
    const hasOm = omFiles.length > 0;

    if (!hasIfc && !hasBep && !hasOm) {
      setError('Please upload at least one file');
      return;
    }

    if (!selectedProjectId) {
      setError('Please select a project. BEP uploads must be linked to a specific project.');
      return;
    }

    const projectName = selectedProject?.name || 'project';
    const projectCode = selectedProject?.code || selectedProjectId;

    setLoading(true);
    setError('');

    try {
      // If BEP only (no IFC) → extract rules async and redirect to the selected project
      if (hasBep && !hasIfc) {
        await runBepExtraction({ force: false, projectName, projectCode });
        return;
      }

      // IFC present → run full audit
      const ifc = ifcFiles[0].file;
      const useChunked = ifc.size > 20 * 1024 * 1024;

      let audit_id;

      if (useChunked) {
        const uploadId = Math.random().toString(36).substring(2, 10);
        await uploadFileChunked(ifc, 'ifc', uploadId);
        if (hasBep) await uploadFileChunked(bepFiles[0].file, 'bep', uploadId);
        for (const f of omFiles) await uploadFileChunked(f.file, 'om', uploadId);

        setProcessingStep('Starting audit pipeline...');
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('project_id', selectedProjectId);
        if (confirmMismatch) form.append('confirm_mismatch', 'true');
        const startResp = await apiFetch(`${API_URL}/api/audits/start-from-chunks`, { method: 'POST', body: form });
        if (startResp.status === 409) {
          const err = await startResp.json().catch(() => ({}));
          if (err.mismatch) { setNamingMismatch({ errors: err.errors || [] }); return; }
          throw new Error(err.detail || 'Failed to start audit');
        }
        if (!startResp.ok) {
          const err = await startResp.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to start audit');
        }
        audit_id = (await startResp.json()).audit_id;
      } else {
        setProcessingStep('Uploading files...');
        const formData = new FormData();
        formData.append('ifc_file', ifc);
        if (hasBep) formData.append('bep_file', bepFiles[0].file);
        omFiles.forEach(f => formData.append('om_files', f.file));
        formData.append('project_id', selectedProjectId);
        if (confirmMismatch) formData.append('confirm_mismatch', 'true');

        const uploadResp = await apiFetch(`${API_URL}/api/audits/upload`, { method: 'POST', body: formData });
        if (uploadResp.status === 409) {
          const errData = await uploadResp.json().catch(() => ({}));
          if (errData.mismatch) { setNamingMismatch({ errors: errData.errors || [] }); return; }
          throw new Error(errData.detail || `Upload failed (${uploadResp.status})`);
        }
        if (!uploadResp.ok) {
          const errData = await uploadResp.json().catch(() => ({}));
          throw new Error(errData.detail || `Upload failed (${uploadResp.status})`);
        }
        audit_id = (await uploadResp.json()).audit_id;
      }

      setAuditId(audit_id);
      // The pipeline is now running on the backend worker. Forward the
      // user to the dedicated progress page (circular gauge + ETA) so
      // they can watch the audit safely — no 10-minute client-side
      // polling timeout, no risk of being silently dropped on
      // ``/results`` while the audit is still working. The progress
      // page handles its own polling and auto-navigates to /results
      // when the audit reaches a terminal state.
      const scope = selectedProjectId
        ? `?project=${encodeURIComponent(selectedProjectId)}`
        : '';
      navigate(`/audits/${audit_id}/progress${scope}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setProcessingStep('');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="audit-page-title">
            Run AHA Quality Gate
          </h1>
          <p className="text-muted-foreground">
            Upload your BIM model, BEP document, and O&M manuals to run an autonomous handover audit
          </p>
        </div>

        {/* Project Selector — required for all uploads */}
        <Card className="mb-6 border-accent/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-accent" />
              Select Project
            </CardTitle>
            <CardDescription className="text-xs">
              All BEP and IFC uploads are linked to a specific project. Same BEP can be reused across projects but won't duplicate within one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {projectsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading projects...
              </div>
            ) : projects.length === 0 ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No projects found. <button onClick={() => navigate('/projects')} className="underline font-medium">Create a project</button> first.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={loading}
                  className="flex-1 min-w-[260px] h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="audit-project-select"
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code}{p.originator ? `-${p.originator}` : ''})
                    </option>
                  ))}
                </select>
                <Button variant="outline" size="sm" onClick={() => navigate('/projects')} disabled={loading}>
                  + New Project
                </Button>
              </div>
            )}
            {selectedProject && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="selected-project-label">
                Uploading to: <span className="font-medium text-foreground">{selectedProject.name}</span> ({selectedProject.code})
              </p>
            )}
          </CardContent>
        </Card>

        {/* Duplicate-BEP info panel — when same hash+version already in registry */}
        {duplicateInfo && (
          <Card className="mb-6 border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/5" data-testid="duplicate-bep-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-[hsl(var(--warning))]" />
                BEP already analyzed for this project
              </CardTitle>
              <CardDescription className="text-xs">
                {duplicateInfo.file_name}
                {duplicateInfo.document_version && <> • Version <strong>{duplicateInfo.document_version}</strong></>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
                <div>
                  <p className="text-muted-foreground uppercase tracking-wider mb-0.5">Project</p>
                  <p className="font-medium">{duplicateInfo.project_name} ({duplicateInfo.project_code})</p>
                </div>
                <div>
                  <p className="text-muted-foreground uppercase tracking-wider mb-0.5">Analyzed</p>
                  <p>{duplicateInfo.analyzed_at ? new Date(duplicateInfo.analyzed_at).toLocaleString() : '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground uppercase tracking-wider mb-0.5">Rules</p>
                  <p><strong>{duplicateInfo.rules_count}</strong> extracted{duplicateInfo.has_stored_rules ? '' : ' (rule details not stored)'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground uppercase tracking-wider mb-0.5">Extracted By</p>
                  <p>{duplicateInfo.extracted_by || '—'}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => navigate(`/projects/${duplicateInfo.slug}`)}
                  data-testid="dup-view-project-btn">
                  View Project & Rules
                </Button>
                <Button variant="outline" onClick={() => setShowReExtract(true)}
                  data-testid="dup-force-reextract-btn">
                  Re-extract Anyway
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDuplicateInfo(null)}
                  data-testid="dup-dismiss-btn">
                  <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                </Button>
              </div>
              {!duplicateInfo.has_stored_rules && (
                <p className="mt-3 text-[11px] text-muted-foreground">
                  Note: this BEP was analyzed before rule-text persistence was enabled. Re-extract to capture the full list.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {duplicateInfo && (
          <ReExtractWarningDialog
            open={showReExtract}
            onOpenChange={setShowReExtract}
            projectId={duplicateInfo.slug}
            registryId={duplicateInfo.registry_id}
            fileName={duplicateInfo.file_name}
            onConfirm={async () => {
              const info = duplicateInfo;
              setDuplicateInfo(null); setLoading(true); setError('');
              try {
                await runBepExtraction({ force: true, projectName: info.project_name, projectCode: info.project_code });
              } catch (e) { setError(e.message); setLoading(false); }
            }}
          />
        )}

        {namingMismatch && (
          <div className="border border-amber-500/50 bg-amber-500/10 rounded-md p-4 mb-6 space-y-2"
            data-testid="naming-mismatch-dialog">
            <p className="text-sm font-semibold text-amber-700">This file may not belong to this project</p>
            <ul className="text-xs text-amber-800 space-y-0.5">
              {namingMismatch.errors.map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setNamingMismatch(null)}
                data-testid="naming-mismatch-cancel-btn">
                Cancel
              </Button>
              <Button size="sm" variant="outline"
                className="border-amber-500/60 text-amber-700 hover:bg-amber-500/20"
                onClick={() => { setNamingMismatch(null); runAudit({ confirmMismatch: true }); }}
                data-testid="naming-mismatch-validate-anyway-btn">
                Validate anyway
              </Button>
            </div>
          </div>
        )}
        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="audit-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
          </Alert>
        )}

        {loading && (
          <Card className="mb-6 border-accent" data-testid="audit-processing">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
                <div className="flex-1">
                  <p className="font-semibold text-foreground">Processing Audit...</p>
                  <p className="text-sm text-muted-foreground mt-1" data-testid="processing-step">{processingStep}</p>
                </div>
              </div>
              <div className="mt-4 space-y-1.5">
                {PROCESSING_STEPS.map((step) => (
                  <div key={step.key} className="flex items-center gap-2 text-xs">
                    {processingStep.toLowerCase().includes(step.key) ? (
                      <Loader2 className="h-3 w-3 animate-spin text-accent" />
                    ) : processingStep && PROCESSING_STEPS.findIndex(s => processingStep.toLowerCase().includes(s.key)) >
                      PROCESSING_STEPS.findIndex(s => s.key === step.key) ? (
                      <CheckCircle2 className="h-3 w-3 text-[hsl(var(--success))]" />
                    ) : (
                      <div className="h-3 w-3 rounded-full border border-muted-foreground/30" />
                    )}
                    <span className="text-muted-foreground">{step.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Upload Documents</CardTitle>
            <CardDescription>
              Drag and drop your project files into the appropriate zones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FileDropZone
                label="IFC Model"
                description="Drop .ifc file (required)"
                icon={FileCode}
                accept={{ 'application/x-step': ['.ifc'], 'application/ifc': ['.ifc'] }}
                files={ifcFiles}
                onDrop={onDropIfc}
                onRemove={() => setIfcFiles([])}
                disabled={loading}
                testId="dropzone-ifc"
              />
              <FileDropZone
                label="BEP Document"
                description="Drop BEP .pdf (optional)"
                icon={FileText}
                accept={{ 'application/pdf': ['.pdf'] }}
                files={bepFiles}
                onDrop={onDropBep}
                onRemove={() => setBepFiles([])}
                disabled={loading}
                testId="dropzone-bep"
              />
              <FileDropZone
                label="O&M Manuals"
                description="Drop O&M .pdf files (optional)"
                icon={BookOpen}
                accept={{ 'application/pdf': ['.pdf'] }}
                files={omFiles}
                onDrop={onDropOm}
                onRemove={(id) => setOmFiles(omFiles.filter(f => f.id !== id))}
                disabled={loading}
                testId="dropzone-om"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button
            onClick={runAudit}
            disabled={loading || !selectedProjectId || (ifcFiles.length === 0 && bepFiles.length === 0 && omFiles.length === 0)}
            size="lg"
            className="px-8"
            data-testid="run-audit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Running Audit...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Run Autonomous Handover Audit
              </>
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
